import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import * as _ from 'lodash';

import AggressiveReadingIcon from 'common/images/AggressiveReadingIcon';
import { TemperatureHalf, Wrench } from 'common/images/FaIcons';
import DataTable from 'common/components/organisms/DataTable';
import { localTimeString } from '../../../common/helpers/time';
import { BatteryIcon } from '../../../common/images/BatteryIcon';
import { PoeIcon } from '../../../common/images/PoeIcon';
import WiFiSvg from '../../../common/images/WiFiSvg';
import Checkbox from '../../../common/components/atoms/Checkbox';
import { getSensorLocationDisplayText } from '../../../common/utils/locationDisplayName';

const CheckBoxContainer = styled.span`
  display: inline-flex;
  margin-right: 0.5em;
  vertical-align: middle;
`;

const SerialCell = styled.span`
  display: inline-flex;
  align-items: center;
`;

const WifiNa = ({ title, text }) => (title ? <div title={title}>{text}</div> : <div>{text}</div>);

WifiNa.propTypes = { title: PropTypes.string, text: PropTypes.string };
WifiNa.defaultProps = { title: null, text: '' };

/**
 * Devices list table. A Devices-specific configuration of the shared DataTable
 * that ports the per-cell rendering previously living in the connected
 * SensorsItem (serial number + master/aggressive/temperature badges and the
 * edit/admin selection checkbox, machine, location, battery, wifi, timestamps,
 * account, firmware). All redux/selection wiring stays in the Sensors page and
 * is passed down as props, so this component is purely presentational.
 */
class DevicesTable extends React.Component {
  getMasterTitle = (item) => {
    const { t } = this.props;
    if (!item.is_master) return null;
    return item.type === 'VSX' ? t('sensors.list.masterVsx') : t('sensors.list.masterMote');
  };

  renderSerialNumber = (item) => {
    const {
      t, editMode, adminOpen, selectedList
    } = this.props;
    const selected = selectedList.includes(item.serial_number);
    let aggressiveIconHoverText = t('sensors.detail.aggressiveHoverBattery');
    if (item.poe || item.tx_poe) {
      aggressiveIconHoverText = t('sensors.detail.aggressiveHoverPoe');
    }
    return (
      <SerialCell className="device-serial-number">
        {(editMode || adminOpen) && (
          <CheckBoxContainer>
            <Checkbox value={selected} />
          </CheckBoxContainer>
        )}
        <span title={this.getMasterTitle(item)}>
          {item.is_master ? `${item.serial_number}*` : item.serial_number}
        </span>
        <span style={{ paddingLeft: '10px' }}>
          {item.aggressive_measurement && (
            <span title={aggressiveIconHoverText} style={{ marginRight: '8px' }}>
              <AggressiveReadingIcon />
            </span>
          )}
          {item.temperature_only && (
            <span title={t('sensors.list.temperatureOnlyModeEnabled')} style={{ marginRight: '3px' }}>
              <TemperatureHalf size={18} />
            </span>
          )}
          {item.debug_mode_active && (
            <span
              title={t('sensors.list.debugModeEnabled', { defaultValue: 'Debug mode active — measurement settings are temporarily overridden' })}
              style={{ marginRight: '3px' }}
            >
              <Wrench />
            </span>
          )}
        </span>
      </SerialCell>
    );
  };

  renderWifi = (item) => {
    const { t } = this.props;
    if (!_.isNull(item.wifi_signal) && item.model && (item.model === 'TX' || item.model.includes('VM') || item.model.includes('GS'))) {
      return <WiFiSvg width={17} height={17} wifiLevel={item.wifi_signal} />;
    }
    if (!item.wifi_signal && item.wifi_rssi) {
      return <WifiNa title={t('sensors.detail.lastWifiRssiTitle', { value: item.wifi_rssi })} text={t('sensors.detail.na')} />;
    }
    if (!item.wifi_signal && !item.wifi_rssi) {
      return <WifiNa text={t('sensors.detail.na')} />;
    }
    return '--';
  };

  renderCell = (name, item) => {
    const { t } = this.props;
    switch (name) {
      case 'serial_number':
        return this.renderSerialNumber(item);
      case 'machine':
        return item.machine || '--';
      case 'location':
        return getSensorLocationDisplayText(t, item.location, item.location_description);
      case 'battery_level':
        return (item.type === 'Transmitter' && item.poe)
          ? <PoeIcon />
          : <BatteryIcon batteryLevel={item.battery_level} />;
      case 'wifi_signal':
        return this.renderWifi(item);
      case 'last_reported_at':
        return localTimeString(item.last_reported_at);
      case 'last_measurement_time':
        return localTimeString(item.last_measurement);
      case 'account_id':
        return item.account_name || '--';
      case 'firmware':
        return item.mcu_firmware || '--';
      default:
        return null;
    }
  };

  buildColumns = () => {
    const { columns, headerLabel, sortDisabled } = this.props;
    return columns.map(name => ({
      name,
      label: headerLabel(name),
      noSorter: sortDisabled,
      render: item => this.renderCell(name, item)
    }));
  };

  render() {
    const {
      rows, sorter, setSorter, sortDisabled, onRowClick,
      selectedList, loading, hasMore, loadMoreItems, emptyMsg, scrollToRowKey
    } = this.props;
    const sort = sorter && sorter.sorter ? sorter.sorter : null;

    return (
      <DataTable
        columns={this.buildColumns()}
        rows={rows}
        getRowKey={item => item.serial_number}
        onRowClick={onRowClick}
        isRowSelected={item => selectedList.includes(item.serial_number)}
        scrollToRowKey={scrollToRowKey}
        sort={sortDisabled ? null : sort}
        onSortChange={sortDisabled ? null : setSorter}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={loadMoreItems}
        emptyMessage={emptyMsg}
      />
    );
  }
}

DevicesTable.propTypes = {
  columns: PropTypes.arrayOf(PropTypes.string).isRequired,
  headerLabel: PropTypes.func.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  sorter: PropTypes.object,
  setSorter: PropTypes.func,
  sortDisabled: PropTypes.bool,
  onRowClick: PropTypes.func.isRequired,
  selectedList: PropTypes.arrayOf(PropTypes.string),
  editMode: PropTypes.bool,
  adminOpen: PropTypes.bool,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  loadMoreItems: PropTypes.func,
  emptyMsg: PropTypes.string,
  scrollToRowKey: PropTypes.string,
  t: PropTypes.func.isRequired
};

DevicesTable.defaultProps = {
  rows: [],
  sorter: null,
  setSorter: () => {},
  sortDisabled: false,
  selectedList: [],
  editMode: false,
  adminOpen: false,
  loading: false,
  hasMore: false,
  loadMoreItems: () => {},
  emptyMsg: 'No results found',
  scrollToRowKey: null
};

export default DevicesTable;
